import React, {
  Fragment,
  useEffect,
  useContext,
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import { Helmet } from 'react-helmet';
import { Button, Close, Input } from 'chaoskit/src/components';
import { config } from 'chaoskit/src/helpers/config';
import qs from 'qs';
import { isEmpty } from 'lodash-es';
import useUpdateEffect from 'react-use/lib/useUpdateEffect';

import BaseLayout from '../layouts/BaseLayout';
import { Icon } from '../components';
import Hero from '../partials/Hero';

import {
  ReliabilityCalculatorProvider,
  ReliabilityCalculatorContext,
} from '../partials/reliability-calculator/Context';
import Step1 from '../partials/reliability-calculator/Step1';
import Step2 from '../partials/reliability-calculator/Step2';
import Step3 from '../partials/reliability-calculator/Step3';
import Step4, {
  getDefaultSLOBasedOnTier,
} from '../partials/reliability-calculator/Step4';
import Step5 from '../partials/reliability-calculator/Step5';
import Step6 from '../partials/reliability-calculator/Step6';
import Step7 from '../partials/reliability-calculator/Step7';
import Step8 from '../partials/reliability-calculator/Step8';
import Step9 from '../partials/reliability-calculator/Step9';
import Step10 from '../partials/reliability-calculator/Step10';
import Step11 from '../partials/reliability-calculator/Step11';

// This is relative to the amount of "steps/questions" we have for the progress bar; not necessarily the actual amount of cells in the slider
const cells = 9;

export const getQueryArgs = search => {
  // If we have query params, add them to inputs that will be sent to Pardot
  const { results } = qs.parse(search, {
    ignoreQueryPrefix: true,
  });

  if (!isEmpty(results)) {
    try {
      const decodedURL = window.atob(results);
      const parsedURL = qs.parse(decodedURL);

      // If we have all of our query params; advance to cell 10
      if (
        parsedURL.step1 &&
        parsedURL.step2 &&
        parsedURL.step3 &&
        parsedURL.step4 &&
        parsedURL.step5 &&
        parsedURL.step6 &&
        parsedURL.step7 &&
        parsedURL.step8 &&
        parsedURL.step9
      ) {
        return parsedURL;
      }
    } catch {
      return {};
    }
  }

  return {};
};

export const ReliabilityHelpTrigger = ({ ...props }) => (
  <span
    role="button"
    tabIndex="-1"
    className="reliabilityCalculator__underline"
    {...props}
  />
);

export const ReliabilityHelp = ({ children, title, toggleHelpOpen }) => (
  <div className="reliabilityCalculator__help u-contrast">
    <h3 className="u-mb--large u-textCenter">
      <span className="reliabilityCalculator__underline">{title}</span>
    </h3>
    <Close
      className="reliabilityCalculator__help-close"
      onClick={() => toggleHelpOpen(false)}
    />
    {children}
  </div>
);

ReliabilityHelp.propTypes = {
  children: PropTypes.node.isRequired,
  title: PropTypes.string.isRequired,
  toggleHelpOpen: PropTypes.func.isRequired,
};

export const ReliabilityOption = ({ title, children, active, ...props }) => (
  <div
    className={cx('reliabilityCalculator__option', {
      [`${config.classes.active} u-contrast`]: active,
    })}
    tabIndex="-1"
    role="button"
    {...props}
  >
    <h4
      className={cx({
        'reliabilityCalculator__underline u-pb--small': children,
      })}
    >
      {title}
    </h4>
    {children}
  </div>
);

ReliabilityOption.propTypes = {
  title: PropTypes.string.isRequired,
  children: PropTypes.node,
  active: PropTypes.bool,
};

export const ReliabilityInput = ({ name, defaultValue, ...props }) => (
  <Input
    className="reliabilityCalculator__input"
    name={name}
    type="number"
    defaultValue={defaultValue}
    min="0"
    onChange={() => 'test'}
    {...props}
  />
);

ReliabilityInput.propTypes = {
  name: PropTypes.string.isRequired,
  defaultValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

const Intro = ({ setActiveCell }) => {
  const [helpOpen, toggleHelpOpen] = useState(false);

  return (
    <div className="reliabilityCalculator__cell">
      {helpOpen ? (
        <ReliabilityHelp title="Tip" toggleHelpOpen={toggleHelpOpen}>
          <p className="u-textCenter">
            Throughout the calculator, click on underlined terms to get more
            information about them.
          </p>
        </ReliabilityHelp>
      ) : (
        <div className="hero-titleWrapper u-contrast">
          <h1 className="hero-title hero-title--large u-mb--large">
            Are you ready for{' '}
            <ReliabilityHelpTrigger onClick={() => toggleHelpOpen(!helpOpen)}>
              chaos?
            </ReliabilityHelpTrigger>
          </h1>
          <div className="hero-titleSub">
            Understanding the relative reliability of your different services
            helps you prioritize efforts to make them more resilient. You can
            use this assessment to rank the overall reliability of each of your
            systems and then get action items to improve them.
          </div>
        </div>
      )}

      <div className="u-textCenter">
        <Button type="primary" onClick={() => setActiveCell(1)}>
          Start<span className="u-hiddenDown@xsmall"> the assessment</span>
        </Button>
      </div>
    </div>
  );
};

Intro.propTypes = {
  setActiveCell: PropTypes.func.isRequired,
};

export const getGrade = (
  step1,
  step2,
  step3,
  step4,
  step5,
  step6,
  step7,
  step8,
  step9
) => {
  // Small utility used for final grading
  const getLetterGrade = (partIIScore, partIIIScore) => {
    let finalCalc = partIIScore * partIIIScore;

    // Bumping up by percentage-factor based on tier level chosen
    if (step1 === 'tier_2') {
      // Increase by 30%
      finalCalc += finalCalc * 0.3;
    } else if (step1 === 'teir_3') {
      // Increase by 70%
      finalCalc += finalCalc * 0.7;
    }

    finalCalc = finalCalc.toFixed(2);

    if (finalCalc < 0.06) {
      return 'F';
    }
    if (finalCalc >= 0.06 && finalCalc <= 0.08) {
      return 'C-';
    }
    if (finalCalc >= 0.09 && finalCalc <= 0.11) {
      return 'C';
    }
    if (finalCalc >= 0.12 && finalCalc <= 0.14) {
      return 'C+';
    }
    if (finalCalc >= 0.15 && finalCalc <= 0.24) {
      return 'B-';
    }
    if (finalCalc >= 0.25 && finalCalc <= 0.39) {
      return 'B';
    }
    if (finalCalc >= 0.4 && finalCalc <= 0.49) {
      return 'B+';
    }
    if (finalCalc >= 0.5 && finalCalc <= 0.54) {
      return 'A-';
    }
    if (finalCalc >= 0.55 && finalCalc <= 0.79) {
      return 'A';
    }
    if (finalCalc >= 0.8) {
      return 'A+';
    }
  };

  // Splitting up scores into separate sections as they are used to influence the recommended articles at the very end

  // Part II scores include steps 2, 3, and 4
  const partIIAvailability =
    Math.abs(parseInt(step2, 10)) /
    (Math.abs(parseInt(step2, 10)) + Math.abs(parseInt(step3, 10)));

  const getPartIISLOAttain = () => {
    let step4Clean = step4;

    // Clean up dirty data user could provide
    if (parseInt(step4, 10) === 0 || parseInt(step4, 10) === '') {
      step4Clean = getDefaultSLOBasedOnTier(step1);
    }

    // Convert percentage to decimal value
    step4Clean /= 100;

    if (step4Clean > 1) {
      step4Clean = 1;
    }

    return step4Clean;
  };

  const partIIScore = () => {
    // If either step2 or step3 are 0 (they checked the box), then set default value for partII
    if (parseInt(step2, 10) === 0 || parseInt(step3, 10) === 0) {
      return 0.25;
    }

    return partIIAvailability / getPartIISLOAttain();
  };

  // Part III score contains 5, 6, 7, 8, and 9
  const getStep5Score = () => {
    // eslint-disable-next-line default-case
    switch (step5) {
      case 'no':
        return 0;
      case 'yes':
        return 0.15;
    }
  };

  const getStep6Score = () => {
    // eslint-disable-next-line default-case
    switch (step6) {
      case 'no':
        return 0;
      case 'yes':
        return 0.1;
    }
  };

  const getStep7Score = () => {
    // eslint-disable-next-line default-case
    switch (step7) {
      case 'no':
        return 0;
      case 'yes-247':
        return 0.25;
      case 'yes-business':
        return 0.1;
    }
  };

  const getStep8Score = () => {
    // eslint-disable-next-line default-case
    switch (step8) {
      case 'no':
        return 0;
      case 'yes-monitoring-alerting':
        return 0.25;
      case 'yes-monitoring':
        return 0.1;
    }
  };

  const getStep9Score = () => {
    // eslint-disable-next-line default-case
    switch (step9) {
      case 'never':
        return 0;
      case 'annually':
        return 0.05;
      case 'quarterly':
        return 0.1;
      case 'monthly':
        return 0.15;
      case 'weekly':
        return 0.2;
      case 'continuously':
        return 0.25;
    }
  };

  const partIIIScore =
    getStep5Score() +
    getStep6Score() +
    getStep7Score() +
    getStep8Score() +
    getStep9Score();

  return getLetterGrade(partIIScore(), partIIIScore);
};

const ReliabilityGrade = () => {
  const {
    state: { step1, step2, step3, step4, step5, step6, step7, step8, step9 },
  } = useContext(ReliabilityCalculatorContext);

  const grade = getGrade(
    step1,
    step2,
    step3,
    step4,
    step5,
    step6,
    step7,
    step8,
    step9
  );

  return (
    <div className="reliabilityCalculator__grade-wrapper">
      <div className="reliabilityCalculator__grade">
        <div className="reliabilityCalculator__grade-letter">
          <span>{grade}</span>
        </div>
        <h5 className="reliabilityCalculator__grade-label">Current Grade</h5>
      </div>
    </div>
  );
};

const ReliabilityCalculatorNav = ({ activeCell, setActiveCell, className }) => {
  const {
    state: { step1, step2, step3, step4, step5, step6, step7, step8, step9 },
  } = useContext(ReliabilityCalculatorContext);

  const checkActiveCell = () => {
    // Checking against empty string and null since 0 can be a valid answer
    switch (activeCell) {
      case 1:
        return step1 === '' || step1 === null;
      case 2:
        return step2 === '' || step2 === null;
      case 3:
        return step3 === '' || step3 === null;
      case 4:
        return step4 === '' || step4 === null;
      case 5:
        return step5 === '' || step5 === null;
      case 6:
        return step6 === '' || step6 === null;
      case 7:
        return step7 === '' || step7 === null;
      case 8:
        return step8 === '' || step8 === null;
      case 9:
        return step9 === '' || step9 === null;
      default:
        return false;
    }
  };

  return (
    <nav className={cx('reliabilityCalculator__nav', className)}>
      <div>
        <Button onClick={() => setActiveCell(activeCell - 1)}>
          <Icon
            className="u-textPrimary u-mr--small"
            size="large"
            icon="caret-left"
          />{' '}
          Prev
        </Button>
      </div>
      <div>
        <Button
          disabled={checkActiveCell()}
          onClick={() => setActiveCell(activeCell + 1)}
        >
          Next{' '}
          <Icon
            className="u-textPrimary u-ml--small"
            size="large"
            icon="caret-right"
            disabled={activeCell === cells}
          />
        </Button>
      </div>
    </nav>
  );
};

ReliabilityCalculatorNav.propTypes = {
  activeCell: PropTypes.number.isRequired,
  setActiveCell: PropTypes.func.isRequired,
  className: PropTypes.string,
};

const ReliabilityCalculator = ({ location }) => {
  const reliabilityCalculatorWrapperRef = useRef();
  // If we get valid query args, that means they are visiting a permalink with existing data; so we can forward them off to the results (and ultimately since we'll hide the form) recommendations area
  const [activeCell, setActiveCell] = useState(0);

  useEffect(() => {
    if (!isEmpty(getQueryArgs(location.search))) {
      setActiveCell(11);
    }
  }, []);

  useUpdateEffect(() => {
    // We only stay on this cell for a couple seconds to "calculate" the score before we move onto the form
    if (activeCell === 10) {
      setTimeout(() => {
        setActiveCell(11);
      }, 2000);
    }
  }, [activeCell]);

  const heroStyles = {
    // Putting this here instead of in my styles because Prettier/Stylelint is being a 💩 and won't let me add `px` to 0 which I need for the calc
    minHeight:
      'calc(var(--rh) - var(--gremlin__announcementBar-height, 0px)) !important',
  };

  return (
    <BaseLayout
      headerProps={{
        sticky: false,
        showNav: false,
        subTitle: 'Chaos Engineering Reliability Tool',
      }}
      noFooter
    >
      <ReliabilityCalculatorProvider>
        <Fragment>
          <Helmet
            title="Reliability Calculator"
            meta={[
              {
                name: 'description',
                content:
                  'Understanding the relative reliability of your different services helps you prioritize efforts to make them more resilient. You can use this calculator to rank the overall reliability of each of your systems and then get action items to improve them.',
              },
              {
                property: 'og:image',
                content:
                  'https://res.cloudinary.com/gremlin/image/upload/c_limit,q_80,w_2000/v1572884103/reliability-calculator-og.png',
              },
            ]}
          />
          <div
            className="reliabilityCalculator__progress"
            style={{
              width: `${(activeCell / cells > 1 ? 1 : activeCell / cells) *
                100}%`,
            }}
          />
          <Hero
            particles={false}
            contrast={false}
            className="reliabilityCalculator__hero"
            style={heroStyles}
          >
            <div className="hero-content">
              <div className="container">
                <div
                  className="reliabilityCalculator__wrapper"
                  ref={reliabilityCalculatorWrapperRef}
                >
                  {activeCell === 0 && <Intro setActiveCell={setActiveCell} />}
                  {activeCell === 1 && <Step1 />}
                  {activeCell === 2 && <Step2 />}
                  {activeCell === 3 && <Step3 />}
                  {activeCell === 4 && <Step4 />}
                  {activeCell === 5 && <Step5 />}
                  {activeCell === 6 && <Step6 />}
                  {activeCell === 7 && <Step7 />}
                  {activeCell === 8 && <Step8 />}
                  {activeCell === 9 && <Step9 />}
                  {activeCell === 10 && <Step10 />}
                  {activeCell === 11 && (
                    <Step11
                      location={location}
                      queryArgs={
                        !isEmpty(getQueryArgs(location.search))
                          ? getQueryArgs(location.search)
                          : {}
                      }
                    />
                  )}
                  {/* We're hiding the nav on the first, and last two slides */}
                  {activeCell > 0 && activeCell !== 10 && activeCell !== 11 && (
                    <ReliabilityCalculatorNav
                      activeCell={activeCell}
                      setActiveCell={setActiveCell}
                      className="u-contrast"
                    />
                  )}
                </div>
              </div>
            </div>
          </Hero>
          {activeCell > 1 && activeCell !== 10 && activeCell !== 11 && (
            <ReliabilityGrade />
          )}
        </Fragment>
      </ReliabilityCalculatorProvider>
      {/* We need this static form here even though it's done in Step11 since Netlify requires the static HTML to be present on the page in order to parse data correctly 🤷‍♂️ */}
      <form
        className="u-hidden"
        name="reliability-calculator"
        data-netlify="true"
        data-netlify-honeypot="bot-field"
      >
        <input type="hidden" name="form-name" value="reliability-calculator" />
        <input type="email" name="email" />
        <input type="text" name="url" />

        <input type="hidden" name="bot-field" />
      </form>
    </BaseLayout>
  );
};

ReliabilityCalculator.propTypes = {
  location: PropTypes.object.isRequired,
};

export default ReliabilityCalculator;
